import { useState, type SyntheticEvent } from 'react';
import { useNavigate, useLocation, Link } from 'react-router';
import { confirmEmail, resendConfirmationCode } from '@/services/auth.service';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import { MdMail, MdKey, MdDirectionsBike } from 'react-icons/md';

export default function ConfirmarEmailPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const email = (location.state as { email?: string })?.email ?? '';

  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const handleSubmit = async (e: SyntheticEvent) => {
    e.preventDefault();
    setError('');

    if (!/^\d{6}$/.test(code)) {
      setError('O código deve ter exatamente 6 dígitos numéricos.');
      return;
    }

    try {
      setIsLoading(true);
      await confirmEmail(email, code);
      navigate('/login');
    } catch (err: unknown) {
      const name = (err as { name?: string })?.name ?? '';
      if (name === 'CodeMismatchException') {
        setError('Código inválido. Verifique o e-mail e tente novamente.');
      } else if (name === 'ExpiredCodeException') {
        setError('Código expirado. Clique em "Reenviar código" para solicitar um novo.');
      } else {
        setError('Não foi possível confirmar o e-mail. Tente novamente.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    setError('');
    setInfo('');
    try {
      setIsLoading(true);
      await resendConfirmationCode(email);
      setInfo(`Código reenviado para ${email}`);
    } catch {
      setError('Não foi possível reenviar o código. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-brand-600 to-brand-800 px-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-brand-100 rounded-full mb-4">
              <MdDirectionsBike className="text-3xl text-brand-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">RandoBR</h1>
            <p className="text-gray-600">Confirmar e-mail</p>
          </div>

          {email && (
            <div className="mb-4 bg-brand-50 border border-brand-200 text-brand-700 px-4 py-3 rounded-lg text-sm flex items-center gap-2">
              <MdMail className="text-lg flex-shrink-0" />
              <span>Enviamos um código de 6 dígitos para <strong>{email}</strong></span>
            </div>
          )}

          {info && (
            <div className="mb-4 bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-lg text-sm">
              {info}
            </div>
          )}

          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-2">
                Código de verificação
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MdKey className="text-gray-400 text-xl" />
                </div>
                <input
                  id="code"
                  type="text"
                  inputMode="numeric"
                  pattern="\d{6}"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replaceAll(/\D/g, ''))}
                  className="input-field pl-10 text-center text-xl tracking-widest"
                  placeholder="000000"
                  disabled={isLoading}
                  autoComplete="one-time-code"
                  required
                />
              </div>
            </div>

            <button type="submit" disabled={isLoading} className="btn-primary w-full flex items-center justify-center gap-2">
              {isLoading ? (
                <>
                  <LoadingSpinner size="sm" />
                  <span>Confirmando...</span>
                </>
              ) : (
                <span>Confirmar</span>
              )}
            </button>

            <div className="text-center">
              <button
                type="button"
                onClick={handleResend}
                disabled={isLoading}
                className="text-sm text-brand-600 hover:text-brand-700 font-medium disabled:opacity-50"
              >
                Reenviar código
              </button>
            </div>
          </form>

          <div className="mt-6 text-center">
            <Link to="/login" className="text-sm text-gray-500 hover:text-gray-700 font-medium">
              Voltar para login
            </Link>
          </div>
        </div>

        <p className="text-center text-white text-sm mt-8">&copy; 2026 RandoBR. Todos os direitos reservados.</p>
      </div>
    </div>
  );
}
